const workflowField = (key, label, options = {}) => ({
  key,
  label,
  source: 'assembly',
  editable: true,
  ...options,
});

const jobField = (key, label, options = {}) => ({
  key,
  label,
  source: 'job',
  editable: false,
  ...options,
});

const generalProjectField = (key, label, options = {}) => ({
  key,
  label,
  source: 'generalProject',
  editable: true,
  ...options,
});

export const ASSEMBLY_SCHEDULE_EDITABLE_FIELDS = Object.freeze([
  'AssemblyProjectStatus',
  'PackingListType',
  'PackingListStatus',
  'PackingListOwner',
  'Assembler',
  'AssemblyType',
  'ControlBoxTransferDate',
]);

export const ASSEMBLY_SCHEDULE_LIST_CONTRACT = Object.freeze({
  response: {
    envelope: [],
  },
});

export const ASSEMBLY_PRIMARY_COLUMNS = [
  jobField('JobNumber', 'Job Number', { editable: false }),
  jobField('ModelNumber', 'Model Number', { editable: false }),
  jobField('ProjectName', 'Project Name', { editable: false }),
  jobField('IssuesComments', 'Issues / Comments', { type: 'textarea' }),
  workflowField('AssemblyProjectStatus', 'Assembly Status', {
    type: 'dropdown',
    dropdownTypes: ['Assembly Project Status'],
  }),
  workflowField('PackingListType', 'Packing List Type', {
    type: 'dropdown',
    dropdownTypes: ['Packing List Type'],
  }),
  workflowField('PackingListStatus', 'Packing List Status', {
    type: 'dropdown',
    dropdownTypes: ['Packing List Status'],
  }),
  workflowField('PackingListOwner', 'Packing List Owner', {
    type: 'dropdown',
    dropdownTypes: ['Packing List Owner'],
  }),
  workflowField('Assembler', 'Assembler', {
    type: 'multi-dropdown',
    dropdownTypes: ['Assembler', 'Assembly Assembler'],
  }),
  workflowField('AssemblyType', 'Assembly Type', {
    type: 'dropdown',
    dropdownTypes: ['Assembly Type'],
  }),
  workflowField('ControlBoxTransferDate', 'Control Box Transfer Date', { type: 'date' }),
  generalProjectField('AssemblyExpectedCompletion', 'Assembly Expected Completion', { type: 'date' }),
  jobField('CurrentPlanShipDate', 'Current Plan Ship Date', { type: 'date' }),
  jobField('JobLocation', 'Job Location'),
  jobField('Notes', 'Notes', { type: 'textarea' }),
  jobField('PaymentTerms', 'Payment Terms'),
];

export const ASSEMBLY_DETAIL_COLUMNS = [
  jobField('ControlPanelDue', 'Control Panel Due', { type: 'date' }),
  jobField('ControlPanelExpectedCompletion', 'Control Panel Expected Completion', { type: 'date' }),
  jobField('HCPumpsDue', 'HC Pumps Due', { type: 'date' }),
  jobField('VTPumpsDue', 'VT Pumps Due', { type: 'date' }),
  jobField('PumpExpectedCompletion', 'Pump Expected Completion', { type: 'date' }),
  jobField('AssemblyDue', 'Assembly Due', { type: 'date' }),
  generalProjectField('AssemblyExpectedCompletion', 'Assembly Expected Completion', { type: 'date' }),
  jobField('TestDue', 'Test Due', { type: 'date' }),
  jobField('NPCSMDue', 'NP CSM Due', { type: 'date' }),
  jobField('NPSuctionLineDue', 'NP Suction Line Due', { type: 'date' }),
  jobField('NPInducerTubeDue', 'NP Inducer Tube Due', { type: 'date' }),
  jobField('NPBMXDue', 'NP BMX Due', { type: 'date' }),
];

export const ASSEMBLY_DUE_COMPLETION_FIELDS = Object.freeze({
  ControlPanelDue: ['ControlPanelDone'],
  HCPumpsDue: ['PumpDone', 'PumpsDone'],
  VTPumpsDue: ['PumpDone', 'PumpsDone'],
  AssemblyDue: ['AssemblyDone'],
  NPCSMDue: ['NPCSMDone'],
  NPSuctionLineDue: ['NPSuctionLineDone'],
  NPInducerTubeDue: ['NPInducerTubeDone'],
  NPBMXDue: ['NPBMXDone'],
});

export const ASSEMBLY_COLUMNS = [
  ...ASSEMBLY_PRIMARY_COLUMNS,
  ...ASSEMBLY_DETAIL_COLUMNS,
];

export const ASSEMBLY_FIELD_BY_KEY = Object.freeze(
  Object.fromEntries(ASSEMBLY_COLUMNS.map((column) => [column.key, column]))
);

export const DEFAULT_ASSEMBLY_COLUMN_VISIBILITY = Object.freeze(
  Object.fromEntries(ASSEMBLY_COLUMNS.map((column) => [column.key, true]))
);

export const ASSEMBLY_VIEW_CATEGORY = 'AssemblyScheduleViews';

export const normalizeText = (value) => String(value ?? '').trim();

export const parseAssemblyMultiSelectValue = (value) => {
  const values = Array.isArray(value)
    ? value
    : normalizeText(value).split(/\s*(?:\||;|,)\s*/);
  const seen = new Set();
  return values
    .map(normalizeText)
    .filter((item) => {
      const normalized = item.toLowerCase();
      if (!item || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
};

export const serializeAssemblyMultiSelectValue = (value) => (
  parseAssemblyMultiSelectValue(value).join(' | ')
);

export const getDropdownDisplayText = (option) => (
  normalizeText(option?.DisplayText || option?.displayText || option?.Value || option?.value)
);

const getDropdownSortOrder = (option) => {
  const sortOrder = Number(option?.SortOrder ?? option?.sortOrder);
  return Number.isFinite(sortOrder) ? sortOrder : Number.MAX_SAFE_INTEGER;
};

const isActiveDropdownOption = (option) => {
  const isActive = option?.IsActive ?? option?.isActive;
  return ![false, 0, '0', 'false'].includes(
    typeof isActive === 'string' ? isActive.toLowerCase().trim() : isActive
  );
};

export const getDropdownOptionsForTypes = (options, dropdownTypes) => {
  const normalizedTypes = new Set(
    (dropdownTypes || []).map((type) => normalizeText(type).toLowerCase())
  );
  const seen = new Set();

  return (Array.isArray(options) ? options : [])
    .map((option, originalIndex) => ({ option, originalIndex }))
    .filter(({ option }) => isActiveDropdownOption(option))
    .filter(({ option }) => normalizedTypes.has(
      normalizeText(option?.DropdownType || option?.dropdownType).toLowerCase()
    ))
    .sort((left, right) => (
      getDropdownSortOrder(left.option) - getDropdownSortOrder(right.option) ||
      left.originalIndex - right.originalIndex
    ))
    .map(({ option }) => option)
    .filter((option) => {
      const label = getDropdownDisplayText(option);
      const normalizedLabel = label.toLowerCase();
      if (!label || seen.has(normalizedLabel)) return false;
      seen.add(normalizedLabel);
      return true;
    });
};

export const mergeUniqueOptionLabels = (...optionGroups) => {
  const seen = new Set();
  return optionGroups
    .flat()
    .map((option) => typeof option === 'string'
      ? option
      : normalizeText(option?.displayName || getDropdownDisplayText(option)))
    .filter((label) => {
      const normalized = label.toLowerCase();
      if (!label || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
};

export const getAssemblyRecordProjectId = (record) => (
  record?.ProjectID ?? record?.projectID ?? record?.ProjectId ?? record?.projectId
);

export const getAssemblyRecordId = (record) => record?.ID ?? record?.id ?? null;

export const buildAssemblyScheduleListUrl = (url, page = 1, limit = 1000) => {
  if (!url) return '';
  const separator = String(url).includes('?') ? '&' : '?';
  return `${url}${separator}page=${page}&projectStatusNotEqual=Shipped&limit=${limit}`;
};

export const normalizeAssemblyScheduleRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.value)) return payload.value;
  return [];
};

export const getAssemblyIssueCommentProjectId = (record) => (
  record?.ProjectID ?? record?.projectID ?? record?.ProjectId ?? record?.projectId
);

export const getAssemblyIssueCommentText = (record) => normalizeText(
  record?.IssueComment ??
  record?.IssueComments ??
  record?.IssuesComments ??
  record?.CommentText ??
  record?.Comment ??
  record?.Comments ??
  record?.Text ??
  record?.Message ??
  record?.Value ??
  record?.Description
);

export const normalizeAssemblyIssueComments = (payload) => {
  const normalizedRows = normalizeAssemblyScheduleRows(payload);
  const rows = normalizedRows.length > 0
    ? normalizedRows
    : payload?.data && typeof payload.data === 'object'
      ? [payload.data]
      : payload && typeof payload === 'object'
        ? [payload]
        : [];

  return rows.flatMap((row) => {
    const children = row?.assemblyIssuesComments ?? row?.AssemblyIssuesComments;
    if (Array.isArray(children)) {
      return children.map((comment) => ({
        ...comment,
        ProjectID: getAssemblyIssueCommentProjectId(comment) ?? row.ID ?? row.ProjectID,
      }));
    }

    return getAssemblyIssueCommentProjectId(row) !== undefined && getAssemblyIssueCommentText(row)
      ? [row]
      : [];
  });
};

export const formatAssemblyIssueComments = (comments) => {
  const seen = new Set();
  return (Array.isArray(comments) ? comments : [])
    .map(getAssemblyIssueCommentText)
    .filter((comment) => {
      const normalized = comment.toLowerCase();
      if (!comment || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    })
    .join(' | ');
};

export const isAssemblyIssueComplete = (comment) => [
  'resolved',
  'closed',
  'complete',
  'completed',
].includes(normalizeText(comment?.Status ?? comment?.status).toLowerCase());

export const buildAssemblyScheduleCreatePayload = (projectId, changes = {}) => ({
  ProjectID: projectId,
  ...Object.fromEntries(ASSEMBLY_SCHEDULE_EDITABLE_FIELDS.map((field) => (
    [field, Object.prototype.hasOwnProperty.call(changes, field) ? changes[field] : null]
  ))),
});

export const buildAssemblySchedulePatchPayload = (record, changes = {}) => ({
  ID: getAssemblyRecordId(record),
  ProjectID: getAssemblyRecordProjectId(record),
  ...Object.fromEntries(Object.entries(changes).filter(([field]) => (
    ASSEMBLY_SCHEDULE_EDITABLE_FIELDS.includes(field)
  ))),
});

export const buildAssemblyScheduleMutation = (record, projectId, changes = {}) => (
  getAssemblyRecordId(record)
    ? {
        method: 'patch',
        payload: buildAssemblySchedulePatchPayload(record, changes),
      }
    : {
        method: 'post',
        payload: buildAssemblyScheduleCreatePayload(projectId, changes),
      }
);

export const getModelHighlightClass = (job) => {
  const explicitHighlight = normalizeText(job?.ModelHighlight).toLowerCase();
  if (['orange', 'yellow', 'stock'].includes(explicitHighlight)) {
    return `assembly-model-${explicitHighlight}`;
  }

  const jobNumber = normalizeText(job?.JobNumber).toUpperCase();
  const model = normalizeText(job?.ModelNumber).toUpperCase();

  if (jobNumber.startsWith('99-01-') || model.includes('STOCK PULSE')) {
    return 'assembly-model-stock';
  }

  if (/^(VTV|VTVE|VTVC|HCL|HCLV|HCBE|HCLVE|RET-HCLV)/.test(model)) {
    return 'assembly-model-yellow';
  }

  if (/^(WMB|WML|VMSBV|BMX|SHMSTV|SHSTV)/.test(model)) {
    return 'assembly-model-orange';
  }

  return '';
};

const ASSEMBLY_PROJECT_STATUS_COLORS = Object.freeze({
  green: new Set([
    'READY TO ASSEMBLE',
    'ASSEMBLY COMPLETE',
    'TEST COMPLETE IN PROCESS AT UL',
    'READY TO TEST',
    'UL COMPLETE',
    'READY TO PACK',
    'READY TO SHIP',
    'FAB. DONE',
    'PANEL DONE',
  ]),
  yellow: new Set([
    'IN PROCESS AT ASSEMBLY',
    'IN PROCESS AT TEST',
    'MFG. ORDER',
    'IN PROCESS AT FAB.',
    'IN PROCESS AT PANEL SHOP',
    'INSTALL ENCLOSURE',
    'IN-Q FOR ASSEMBLY',
  ]),
  red: new Set([
    'TO BE DETERMINED',
    'PART REQUIRED ON ORDER',
    'PARTS TO PICK',
    'TEST REQUIRED',
  ]),
  black: new Set([
    'FAB. SHOP',
    'PANEL SHOP',
    'N/A',
    'INTEGRITY',
    'OTHER',
  ]),
});

export const getProjectStatusClass = (value) => {
  const normalized = normalizeText(value).replace(/\s+/g, ' ').toUpperCase();
  if (!normalized) return '';

  const color = Object.entries(ASSEMBLY_PROJECT_STATUS_COLORS)
    .find(([, statuses]) => statuses.has(normalized))?.[0] || 'black';

  return `assembly-status-${color}`;
};

export const getIssuesCommentsClass = (value) => {
  const normalized = normalizeText(value).toUpperCase();
  return /(\$|\bHOLD\b|\bREQ(?:UIRED)?\.?|HERE\?)/.test(normalized)
    ? 'assembly-issue-attention'
    : '';
};

export const getPackingListStatusClass = (value) => {
  const normalized = normalizeText(value).replace(/\s+/g, ' ').toUpperCase();

  if (['COMPLETE', 'READY TO PACK AT SHIPPING', 'READY TO SHIP'].includes(normalized)) {
    return 'assembly-status-green';
  }
  if (normalized === 'IN PROGRESS') return 'assembly-status-yellow';
  if (normalized === 'REQUIRED') return 'assembly-status-red';
  return '';
};

export const isBlankValue = (value) => value === null || value === undefined || value === '';

export const getAssemblyDueCompletionValue = (job, dueField) => {
  const completionFields = ASSEMBLY_DUE_COMPLETION_FIELDS[dueField] || [];
  const completedField = completionFields.find((field) => !isBlankValue(job?.[field]));
  return completedField ? job[completedField] : null;
};

export const formatAssemblyDate = (value) => {
  if (isBlankValue(value)) return '';
  const isoDate = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) return `${isoDate[2]}/${isoDate[3]}/${isoDate[1].slice(-2)}`;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
};

export const formatAssemblyDateForInput = (value) => {
  if (isBlankValue(value)) return '';
  const isoDate = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDate) return isoDate[1];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const normalizeAssemblyRecords = (payload) => {
  const normalizedRows = normalizeAssemblyScheduleRows(payload);
  const rows = normalizedRows.length > 0
    ? normalizedRows
    : payload?.data && typeof payload.data === 'object'
      ? Object.values(payload.data)
      : payload && typeof payload === 'object'
        ? Object.values(payload)
        : [];

  return rows.flatMap((row) => {
    const nestedRecord = row?.assemblySchedule ?? row?.AssemblySchedule;
    if (nestedRecord && typeof nestedRecord === 'object') {
      return [{
        ...nestedRecord,
        ProjectID: getAssemblyRecordProjectId(nestedRecord) ?? row.ID ?? row.ProjectID,
      }];
    }

    const isDirectRecord = getAssemblyRecordProjectId(row) !== undefined && (
      getAssemblyRecordId(row) !== null ||
      ASSEMBLY_SCHEDULE_EDITABLE_FIELDS.some((field) => Object.prototype.hasOwnProperty.call(row, field))
    );
    return isDirectRecord ? [row] : [];
  });
};
